import React from 'react';
import { motion } from 'framer-motion';
import { TruckIcon, PackageIcon, ArrowRightIcon, WifiIcon, DollarSignIcon, FileTextIcon, HeadphonesIcon, SparklesIcon, UserPlusIcon, RefreshCwIcon } from 'lucide-react';
const benefits = [{
  icon: WifiIcon,
  title: 'Internet that you can really, really rely on.',
  description: "Say hello to reliable internet plans that allow you to get online with ease. All our plans come with no annual contracts and unlimited data. So you can enjoy connectivity without complexity. Find out if we're available in your area.",
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}, {
  icon: DollarSignIcon,
  title: 'Simple, straightforward pricing.',
  description: "Moving can be expensive. We're dedicated to providing simple connectivity with straightforward pricing. Starting at just $50/mo (plus taxes)*, you can enjoy unlimited data and no introductory pricing, making budgeting for your move a whole lot easier.",
  color: 'text-green-500',
  bg: 'bg-green-50'
}, {
  icon: FileTextIcon,
  title: 'No annual contracts, no surprises.',
  description: 'All Ambient Telecom plans come with no annual contracts, so that you can switch internet providers without the commitment. With Ambient Telecom, nothing can hold you down.',
  color: 'text-purple-500',
  bg: 'bg-purple-50'
}, {
  icon: HeadphonesIcon,
  title: "You're moving, but we're not going anywhere.",
  description: "As a new or existing Ambient Telecom customer, you'll be supported by our customer care team every step of the way. From our installation technicians to our support teams, we're committed to providing the very best experience for you.",
  color: 'text-amber-500',
  bg: 'bg-amber-50'
}];
export function MovingSoonPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div data-id="element-714">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-slate-900" data-id="element-715">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-716"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-717"></div>
        <div className="absolute top-0 right-1/4 -translate-y-1/4 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-718"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-719">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-720">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-721">
              <TruckIcon className="w-4 h-4 mr-2 text-orange-400" data-id="element-722" />
              Hassle-Free Moving
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-723">
              Moving? Internet by Ambient Telecom
              <br className="hidden md:block" data-id="element-724" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400" data-id="element-725">
                will be the easiest part.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed mb-10" data-id="element-726">
              Check if we're where you are. We'll take care of the rest.
            </p>
            <button onClick={() => onNavigate('home')} className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-727">
              Check Availability
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-728" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* New vs Existing */}
      <section className="py-20 bg-white" data-id="element-729">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-730">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-731">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-slate-50 rounded-2xl p-10 border border-slate-100 text-center relative overflow-hidden" data-id="element-732">
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-100 rounded-bl-full opacity-50" data-id="element-733"></div>
              <div className="relative z-10" data-id="element-734">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-735">
                  <UserPlusIcon className="w-8 h-8 text-blue-600" data-id="element-736" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-737">
                  New to Ambient Telecom?
                </h3>
                <p className="text-slate-600 leading-relaxed mb-8" data-id="element-738">
                  Getting your internet installed is as easy as 1-2-3. We'll
                  handle the setup so you can focus on settling in.
                </p>
                <button onClick={() => onNavigate('home')} className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-full font-semibold transition-colors inline-flex items-center" data-id="element-739">
                  Get Started
                  <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-740" />
                </button>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-slate-50 rounded-2xl p-10 border border-slate-100 text-center relative overflow-hidden" data-id="element-741">
              <div className="absolute top-0 right-0 w-24 h-24 bg-green-100 rounded-bl-full opacity-50" data-id="element-742"></div>
              <div className="relative z-10" data-id="element-743">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-744">
                  <RefreshCwIcon className="w-8 h-8 text-green-600" data-id="element-745" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-746">
                  Existing customer?
                </h3>
                <p className="text-slate-600 leading-relaxed mb-8" data-id="element-747">
                  Transfer your internet without the heavy lifting. We make
                  moving your service seamless and stress-free.
                </p>
                <button onClick={() => onNavigate('contact')} className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-full font-semibold transition-colors inline-flex items-center" data-id="element-748">
                  Transfer Service
                  <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-749" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Divider statement */}
      <section className="py-12 bg-slate-50" data-id="element-750">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-751">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-752">
            <SparklesIcon className="w-8 h-8 text-primary mx-auto mb-4" data-id="element-753" />
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900" data-id="element-754">
              Moving is stressful. Getting connected shouldn't be.
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white" data-id="element-755">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-756">
          <div className="space-y-8" data-id="element-757">
            {benefits.map((benefit, i) => {
            const Icon = benefit.icon;
            const isEven = i % 2 === 0;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.05
            }} className={`flex flex-col md:flex-row items-start gap-6 bg-slate-50 rounded-2xl p-8 md:p-10 border border-slate-100 ${isEven ? '' : 'md:flex-row-reverse'}`} data-id="element-758">
                  <div className="flex-shrink-0" data-id="element-759">
                    <div className={`w-14 h-14 ${benefit.bg} rounded-2xl flex items-center justify-center`} data-id="element-760">
                      <Icon className={`w-7 h-7 ${benefit.color}`} data-id="element-761" />
                    </div>
                  </div>
                  <div data-id="element-762">
                    <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-3" data-id="element-763">
                      {benefit.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed" data-id="element-764">
                      {benefit.description}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Pricing highlight */}
      <section className="py-16 bg-slate-50" data-id="element-765">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-766">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-slate-900 rounded-3xl p-10 md:p-16 text-center relative overflow-hidden" data-id="element-767">
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/15 rounded-full blur-[80px] pointer-events-none" data-id="element-768"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-500/10 rounded-full blur-[60px] pointer-events-none" data-id="element-769"></div>
            <div className="relative z-10" data-id="element-770">
              <div className="text-sm font-bold text-orange-400 uppercase tracking-widest mb-3" data-id="element-771">
                Starting at
              </div>
              <div className="text-5xl md:text-6xl font-extrabold text-white mb-2" data-id="element-772">
                $50<span className="text-2xl text-slate-400" data-id="element-773">/mo</span>
              </div>
              <p className="text-slate-400 mb-8" data-id="element-774">
                (plus taxes)* — Unlimited data, no introductory pricing
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-sm" data-id="element-775">
                {['No annual contracts', 'Unlimited data', 'No hidden fees', '24/7 support'].map((item, i) => <div key={i} className="flex items-center gap-2 text-slate-300" data-id="element-776">
                    <div className="w-1.5 h-1.5 bg-orange-400 rounded-full" data-id="element-777"></div>
                    {item}
                  </div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary relative overflow-hidden py-20" data-id="element-778">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-779">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl" data-id="element-780"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl" data-id="element-781"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10" data-id="element-782">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-783">
            Ready to make your move easier?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto" data-id="element-784">
            Whether you're a new or existing customer, we'll make sure your
            internet is ready when you are.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-785">
            <button onClick={() => onNavigate('home')} className="w-full sm:w-auto bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-786">
              Get Started
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-787" />
            </button>
            <a href="tel:+4105255284" className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-lg transition-colors" data-id="element-788">
              Call Us: +410-525-5284
            </a>
          </div>
        </div>
      </section>
    </div>;
}